import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Sparkles, ExternalLink } from 'lucide-react';

export default function LichessPage() {
  const [username, setUsername] = useState('');
  const [linked, setLinked] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadMe = () => api.get('/lichess/me').then(r => {
    setLinked(r.data);
    if (r.data.lichess_username) loadGames(r.data.lichess_username);
  });
  const loadGames = (u) => api.get(`/lichess/games/${u}`).then(r => setGames(r.data.games || [])).catch(()=>{});

  useEffect(() => { loadMe(); }, []);

  const link = async () => {
    if (!username.trim()) return;
    setLoading(true);
    try {
      await api.post('/lichess/link', { username: username.trim() });
      toast.success('Cuenta vinculada');
      await loadMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo vincular');
    } finally { setLoading(false); }
  };

  const perfs = linked?.lichess_data?.perfs || {};
  const displayedPerfs = ['bullet','blitz','rapid','classical','puzzle'].filter(p => perfs[p]);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <PageHeader title="Lichess" subtitle="Vincula tu cuenta pública" testId="lichess-header"/>
      {!linked?.lichess_username ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-6 max-w-lg">
          <div className="text-sm text-slate-600 mb-3">
            Ingresa tu nombre de usuario público de Lichess para mostrar tus estadísticas y últimas partidas.
            No se solicita ni almacena tu contraseña.
          </div>
          <div className="flex gap-2">
            <Input data-testid="lichess-username" placeholder="p. ej. CarlosChess" value={username} onChange={(e)=>setUsername(e.target.value)}/>
            <Button data-testid="lichess-link-btn" onClick={link} disabled={loading}
              className="rounded-full bg-blue-600 hover:bg-blue-700 border-b-4 border-blue-800 btn-tactile">
              <Sparkles className="w-4 h-4 mr-1"/> Vincular
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-blue-900 text-white p-8 mb-6">
            <div className="text-xs uppercase tracking-widest text-white/60 font-bold">Cuenta vinculada</div>
            <div className="font-display text-4xl font-extrabold mt-1">@{linked.lichess_username}</div>
            <a href={`https://lichess.org/@/${linked.lichess_username}`} target="_blank" rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-sm text-white/80 hover:text-white">
              Ver en Lichess <ExternalLink className="w-3 h-3"/>
            </a>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
              {displayedPerfs.map(p => (
                <div key={p} className="bg-white/10 rounded-2xl p-4">
                  <div className="text-xs uppercase tracking-widest text-white/60 font-bold">{p}</div>
                  <div className="font-display text-2xl font-extrabold">{perfs[p].rating}</div>
                  <div className="text-xs text-white/60 font-mono">{perfs[p].games} juegos</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <h2 className="font-display font-bold text-xl mb-4">Últimas partidas</h2>
            {games.length === 0 ? (
              <div className="text-sm text-slate-500">Cargando partidas…</div>
            ) : (
              <div className="divide-y">
                {games.map((g, i) => (
                  <div key={i} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-semibold">{g.players?.white?.user?.name || 'Anon'} vs {g.players?.black?.user?.name || 'Anon'}</div>
                      <div className="text-xs text-slate-500 font-mono">{g.speed} · {g.status}</div>
                    </div>
                    <div className="font-mono font-bold">{g.winner ? `${g.winner} gana` : 'tablas'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
